import { SECTION_HEADER_CLASS } from "../ui/section-header";
import { useState, useMemo } from "react";
import type {
  JournalLink,
  JournalEntry,
  JournalEntryType,
} from "@roll1/shared";
import { JOURNAL_TYPE_ICONS } from "../../lib/journal-utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, X, Link2, ExternalLink } from "lucide-react";
import { useI18n } from "../../i18n";
import { searchFilter } from "../../lib/search/engine";

const PRESET_RELATIONS = [
  "lives_in",
  "quest_giver",
  "member_of",
  "enemy_of",
] as const;

interface LinksSectionProps {
  sourceId: string;
  linksFrom: JournalLink[];
  linksTo?: JournalLink[];
  entries: JournalEntry[];
  onLink: (targetId: string, relation: string) => void;
  onUnlink: (targetId: string, relation: string) => void;
  onNavigateToEntry: (entryId: string) => void;
}

export default function LinksSection({
  sourceId,
  linksFrom,
  linksTo = [],
  entries,
  onLink,
  onUnlink,
  onNavigateToEntry,
}: LinksSectionProps) {
  const { t } = useI18n();
  const [isAdding, setIsAdding] = useState(false);
  const [selectedRelation, setSelectedRelation] = useState<string>(
    PRESET_RELATIONS[0],
  );
  const [customRelation, setCustomRelation] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [isCustomRelation, setIsCustomRelation] = useState(false);

  const filteredEntries = useMemo(() => {
    if (!searchQuery.trim()) return [];
    // The app's one matcher (group M): typos, ё/е, best first.
    return searchFilter(searchQuery, entries.filter((e) => e.id !== sourceId), (e) => [e.name]).slice(0, 20);
  }, [entries, sourceId, searchQuery]);

  const effectiveRelation = isCustomRelation
    ? customRelation.trim()
    : selectedRelation;

  const handleAdd = () => {
    if (!selectedTargetId || !effectiveRelation) return;
    onLink(selectedTargetId, effectiveRelation);
    resetForm();
  };

  const resetForm = () => {
    setIsAdding(false);
    setSelectedRelation(PRESET_RELATIONS[0]);
    setCustomRelation("");
    setSearchQuery("");
    setSelectedTargetId(null);
    setIsCustomRelation(false);
  };

  const getEntryName = (entryId: string) => {
    return entries.find((e) => e.id === entryId)?.name ?? entryId;
  };

  const getEntryType = (entryId: string): JournalEntryType | undefined => {
    return entries.find((e) => e.id === entryId)?.type;
  };

  const getRelationLabel = (relation: string) => {
    if (
      PRESET_RELATIONS.includes(relation as (typeof PRESET_RELATIONS)[number])
    ) {
      return t(`journal.relation.${relation}` as any);
    }
    return relation;
  };

  return (
    <div className="space-y-2 pt-3 border-t border-line">
      <div className="flex items-center justify-between">
        <span className={`${SECTION_HEADER_CLASS} flex items-center gap-1`}>
          <Link2 size={14} />
          {t("journal.links.title")}
        </span>
        {!isAdding && (
          <Button
            variant="ghost"
            size="sm"
            className="w-6 p-0"
            onClick={() => setIsAdding(true)}
          >
            <Plus size={14} />
          </Button>
        )}
      </div>

      {/* Existing links */}
      {linksFrom.length === 0 && !isAdding && (
        <p className="text-meta text-dim">{t("journal.links.empty")}</p>
      )}

      {linksFrom.map((link) => {
        const targetType = getEntryType(link.targetId);
        const TargetIcon = targetType
          ? JOURNAL_TYPE_ICONS[targetType]
          : undefined;
        return (
          <div
            key={`${link.targetId}-${link.relation}`}
            className="flex items-center gap-1 text-meta group"
          >
            <span className="text-dim shrink-0">
              {getRelationLabel(link.relation)}
            </span>
            <span className="text-dim shrink-0">→</span>
            {TargetIcon && (
              <TargetIcon size={12} className="shrink-0 text-muted-foreground" />
            )}
            <button
              className="text-hl hover:underline truncate text-left"
              onClick={() => onNavigateToEntry(link.targetId)}
            >
              {getEntryName(link.targetId)}
            </button>
            <Button
              variant="ghost"
              size="sm"
              className="w-5 p-0 opacity-0 group-hover:opacity-100 ml-auto shrink-0"
              onClick={() => onUnlink(link.targetId, link.relation)}
            >
              <X size={12} />
            </Button>
          </div>
        );
      })}

      {/* Incoming links */}
      {linksTo.length > 0 && (
        <>
          <span className="text-label font-semibold uppercase tracking-label text-dim mt-2 block">
            {t("journal.links.incoming")}
          </span>
          {linksTo.map((link) => {
            const sourceType = getEntryType(link.sourceId);
            const SourceIcon = sourceType
              ? JOURNAL_TYPE_ICONS[sourceType]
              : undefined;
            return (
              <div
                key={`${link.sourceId}-${link.relation}`}
                className="flex items-center gap-1 text-meta"
              >
                {SourceIcon && (
                  <SourceIcon size={12} className="shrink-0 text-muted-foreground" />
                )}
                <button
                  className="text-hl hover:underline truncate text-left"
                  onClick={() => onNavigateToEntry(link.sourceId)}
                >
                  {getEntryName(link.sourceId)}
                </button>
                <span className="text-dim shrink-0">→</span>
                <span className="text-dim shrink-0">
                  {getRelationLabel(link.relation)}
                </span>
              </div>
            );
          })}
        </>
      )}

      {/* Add link form */}
      {isAdding && (
        <div className="space-y-2 bg-panel rounded p-2">
          {/* Relation selector */}
          <div className="flex gap-1">
            <Select
              value={isCustomRelation ? "__custom__" : selectedRelation}
              onValueChange={(v) => {
                if (v === "__custom__") {
                  setIsCustomRelation(true);
                } else {
                  setIsCustomRelation(false);
                  setSelectedRelation(v);
                }
              }}
            >
              <SelectTrigger className="h-7 text-meta flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_RELATIONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`journal.relation.${r}` as any)}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">
                  {t("journal.links.customRelation")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isCustomRelation && (
            <Input
              className="h-7 text-meta"
              placeholder={t("journal.links.customRelation")}
              value={customRelation}
              onChange={(e) => setCustomRelation(e.target.value)}
            />
          )}

          {/* Target entry autocomplete */}
          <div className="relative">
            <Input
              className="h-7 text-meta"
              placeholder={t("journal.links.target")}
              value={
                selectedTargetId ? getEntryName(selectedTargetId) : searchQuery
              }
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedTargetId(null);
              }}
            />
            {searchQuery.trim() &&
              !selectedTargetId &&
              filteredEntries.length > 0 && (
                <div className="absolute z-[var(--z-dialog)] top-full left-0 right-0 mt-1 bg-panel-2 border border-line rounded max-h-40 overflow-y-auto">
                  {filteredEntries.map((entry) => {
                    const EntryIcon = JOURNAL_TYPE_ICONS[entry.type];
                    return (
                      <button
                        key={entry.id}
                        className="flex items-center gap-2 w-full px-2 py-1 text-meta hover:bg-line text-left"
                        onClick={() => {
                          setSelectedTargetId(entry.id);
                          setSearchQuery("");
                        }}
                      >
                        <EntryIcon
                          size={12}
                          className="shrink-0 text-muted-foreground"
                        />
                        <span className="truncate">{entry.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
          </div>

          {/* Actions */}
          <div className="flex gap-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetForm}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              disabled={!selectedTargetId || !effectiveRelation}
              onClick={handleAdd}
            >
              {t("journal.links.add")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
